import React, { useState } from 'react';
import { X, CreditCard, CheckCircle, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';

const RAZORPAY_LINK = process.env.REACT_APP_RAZORPAY_LINK || 'https://rzp.io/rzp/hgWhu8T4';

/**
 * Payment modal that directs user to Razorpay first,
 * then lets them confirm payment to unlock calendar booking.
 *
 * Props:
 * - isOpen: boolean to control visibility
 * - onClose: function to close the modal
 * - onSuccess: function called when user confirms payment completed
 */
const PaymentModal = ({ isOpen, onClose, onSuccess }) => {
  const [paymentInitiated, setPaymentInitiated] = useState(false);

  if (!isOpen) return null;

  const handlePayNow = () => {
    // Open Razorpay payment link in a new tab
    window.open(RAZORPAY_LINK, '_blank');
    // Show confirmation step
    setPaymentInitiated(true);
  };

  const handlePaymentDone = () => {
    // User confirms they've completed payment — open calendar
    if (onSuccess) onSuccess();
    if (onClose) onClose();
    setPaymentInitiated(false);
  };

  const handleClose = () => {
    setPaymentInitiated(false);
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative animate-fade-in overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
          >
            <X size={20} />
          </button>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <CreditCard className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Book a Consultation</h2>
              <p className="text-emerald-100 text-sm">Complete payment to reserve your slot</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {!paymentInitiated ? (
            <>
              {/* Step 1 — Pay */}
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-semibold text-slate-800">Complete Payment</p>
                    <p className="text-sm text-slate-500">Click below to securely pay via Razorpay in a new tab.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 opacity-50">
                  <div className="flex-shrink-0 w-7 h-7 bg-slate-100 text-slate-500 rounded-full flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-semibold text-slate-800">Book Your Slot</p>
                    <p className="text-sm text-slate-500">After payment, choose your preferred time on the calendar.</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handlePayNow}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-6 text-base shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <CreditCard className="mr-2 h-5 w-5" />
                Pay Now with Razorpay
              </Button>

              <p className="text-xs text-slate-400 text-center mt-3">
                🔒 100% secure payment powered by Razorpay
              </p>
            </>
          ) : (
            <>
              {/* Step 2 — Confirm & Book Calendar */}
              <div className="text-center space-y-4 mb-6">
                <div className="bg-emerald-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Payment Tab Opened!</h3>
                  <p className="text-slate-500 text-sm mt-1">
                    Please complete your payment in the Razorpay tab. Once done, click below to book your calendar slot.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handlePaymentDone}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-6 text-base shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  I've Paid — Book My Slot
                </Button>
                <button
                  onClick={handlePayNow}
                  className="w-full text-sm text-emerald-600 hover:text-emerald-700 underline underline-offset-2 transition-colors"
                >
                  Re-open payment page
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
